import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import leaveRequestService, { LeaveRequest, LeaveRequestStatus } from '../../services/leaveRequestService';
import employeeService, { Employee } from '../../services/employeeService';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import { getServerErrorMessage } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './HrListPage.css';
import { CirclePlus, RefreshCw, Check, X, Ban, CalendarDays, CircleCheck, Clock } from 'lucide-react';

const MAX_REVIEW_NOTES = 500;

const STATUS_FILTERS = ['Pending', 'Approved', 'Rejected', 'Cancelled'];

const LeaveRequestListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();

  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? null
  );

  const [filterEmployeeId, setFilterEmployeeId] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState('');

  // Review modal — approving and rejecting share the same note-taking dialog.
  const [reviewTarget, setReviewTarget] = useState<LeaveRequest | null>(null);
  const [reviewApprove, setReviewApprove] = useState(true);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);

  const canReview = hasPermission(Permissions.LeaveRequests.Review);
  const canCreate = hasPermission(Permissions.LeaveRequests.Create);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { items } = await leaveRequestService.getAll({
        employeeId: filterEmployeeId === '' ? undefined : Number(filterEmployeeId),
        status: filterStatus || undefined,
      });
      setRequests(items);
    } catch (err) {
      setError(getServerErrorMessage(err) || t('leaveRequest.failedToLoad'));
    }
  }, [filterEmployeeId, filterStatus, t]);

  const loadEmployees = useCallback(async () => {
    try {
      const { items } = await employeeService.getAll();
      setEmployees(items);
    } catch (err) {
      setError(getServerErrorMessage(err) || t('leaveRequest.failedToLoadEmployees'));
    }
  }, [t]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  // Clear the one-shot banner from navigation state so a browser refresh does not replay it.
  useEffect(() => {
    if ((location.state as { message?: string } | null)?.message) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const openReview = (request: LeaveRequest, approve: boolean) => {
    setReviewTarget(request);
    setReviewApprove(approve);
    setReviewNotes('');
  };

  const submitReview = async () => {
    if (!reviewTarget) return;
    setError(null);
    setReviewSaving(true);
    try {
      await leaveRequestService.review(reviewTarget.id, {
        approve: reviewApprove,
        reviewNotes: reviewNotes || undefined,
      });
      setSuccessMessage(reviewApprove ? t('leaveRequest.approveSuccess') : t('leaveRequest.rejectSuccess'));
      setReviewTarget(null);
      await load();
    } catch (err) {
      setError(getServerErrorMessage(err) || t('leaveRequest.failedToReview'));
    } finally {
      setReviewSaving(false);
    }
  };

  const handleCancel = async (request: LeaveRequest) => {
    if (!window.confirm(t('leaveRequest.confirmCancel'))) return;
    try {
      await leaveRequestService.cancel(request.id);
      setSuccessMessage(t('leaveRequest.cancelSuccess'));
      await load();
    } catch (err) {
      setError(getServerErrorMessage(err) || t('leaveRequest.failedToCancel'));
    }
  };

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(value));

  /** Leave is counted in whole days, both ends inclusive. */
  const dayCount = (request: LeaveRequest) => {
    const start = new Date(request.startDate).getTime();
    const end = new Date(request.endDate).getTime();
    return Math.max(1, Math.round((end - start) / 86400000) + 1);
  };

  const statusBadge = (status: LeaveRequestStatus) => {
    if (status === LeaveRequestStatus.Approved) {
      return <span className="ims-badge ims-badge-approved">{t('leaveRequest.status.approved')}</span>;
    }
    if (status === LeaveRequestStatus.Rejected) {
      return <span className="ims-badge ims-badge-rejected">{t('leaveRequest.status.rejected')}</span>;
    }
    if (status === LeaveRequestStatus.Cancelled) {
      return <span className="ims-badge ims-badge-closed">{t('leaveRequest.status.cancelled')}</span>;
    }
    return <span className="ims-badge ims-badge-pending">{t('leaveRequest.status.pending')}</span>;
  };

  const columns: GridColumn[] = [
    {
      key: 'employeeName',
      label: t('leaveRequest.employee'),
      sortable: true,
      width: '190px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'leaveType',
      label: t('leaveRequest.leaveType'),
      sortable: true,
      width: '130px',
      render: (value) => t(`leaveRequest.types.${String(value).toLowerCase()}`, { defaultValue: value }),
    },
    {
      key: 'startDate',
      label: t('leaveRequest.startDate'),
      sortable: true,
      width: '140px',
      render: (value) => formatDate(value as string),
    },
    {
      key: 'endDate',
      label: t('leaveRequest.endDate'),
      sortable: true,
      width: '140px',
      render: (value) => formatDate(value as string),
    },
    {
      key: 'reason',
      label: t('leaveRequest.days'),
      width: '90px',
      render: (_value, row) => dayCount(row as LeaveRequest),
    },
    {
      key: 'status',
      label: t('common.status'),
      width: '130px',
      render: (value) => statusBadge(value as LeaveRequestStatus),
    },
  ];

  const isPending = (row: any) => (row as LeaveRequest).status === LeaveRequestStatus.Pending;

  const actions: GridAction[] = [
    ...(canReview ? [
      {
        icon: <Check size={16} strokeWidth={2} />,
        onClick: (item: any) => openReview(item as LeaveRequest, true),
        className: 'grid-action-btn-view',
        title: t('leaveRequest.approve'),
        hidden: (row: any) => !isPending(row),
      },
      {
        icon: <X size={16} strokeWidth={2} />,
        onClick: (item: any) => openReview(item as LeaveRequest, false),
        className: 'grid-action-btn-delete',
        title: t('leaveRequest.reject'),
        hidden: (row: any) => !isPending(row),
      },
    ] : []),
    ...(canCreate ? [{
      icon: <Ban size={16} strokeWidth={1.75} />,
      onClick: (item: any) => handleCancel(item as LeaveRequest),
      className: 'grid-action-btn-edit',
      title: t('leaveRequest.cancel'),
      hidden: (row: any) => !isPending(row),
    }] : []),
  ];

  const pendingCount = requests.filter((r) => r.status === LeaveRequestStatus.Pending).length;
  const approvedCount = requests.filter((r) => r.status === LeaveRequestStatus.Approved).length;

  return (
    <Layout>
      <div className="container hr-page">
        <div className="header">
          <h1 className="title">{t('leaveRequest.title')}</h1>
          <div className="header-actions">
            {canCreate && (
              <button
                className="btn-new"
                onClick={() => navigate('/hr/leave-requests/create')}
                title={t('leaveRequest.newRequest')}
              >
                <CirclePlus />
                {t('leaveRequest.newRequest')}
              </button>
            )}
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon hr-icon-total"><CalendarDays size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{requests.length}</span>
              <span className="ims-stat-label">{t('leaveRequest.totalRequests')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon hr-icon-open"><Clock size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{pendingCount}</span>
              <span className="ims-stat-label">{t('leaveRequest.status.pending')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon hr-icon-active"><CircleCheck size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{approvedCount}</span>
              <span className="ims-stat-label">{t('leaveRequest.status.approved')}</span>
            </div>
          </div>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show">
            {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title">{t('leaveRequest.filters')}</span>
          </div>
          <div className="ims-card-body">
            <div className="hr-filter-bar">
              <div className="hr-filter-group">
                <span className="hr-filter-label">{t('leaveRequest.employee')}</span>
                <select
                  className="form-select"
                  value={filterEmployeeId}
                  onChange={(e) => setFilterEmployeeId(e.target.value === '' ? '' : Number(e.target.value))}
                >
                  <option value="">{t('leaveRequest.allEmployees')}</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.employeeCode} — {employee.fullName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="hr-filter-group">
                <span className="hr-filter-label">{t('common.status')}</span>
                <select
                  className="form-select"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">{t('leaveRequest.allStatuses')}</option>
                  {STATUS_FILTERS.map((status) => (
                    <option key={status} value={status}>
                      {t(`leaveRequest.status.${status.toLowerCase()}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <DataGrid
          data={requests}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('leaveRequest.noRequestsFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />

        {reviewTarget && (
          <div className="ims-modal-overlay" role="dialog" aria-modal="true">
            <div className="ims-modal">
              <h2 className="ims-modal-title">
                {reviewApprove ? t('leaveRequest.approveTitle') : t('leaveRequest.rejectTitle')}
              </h2>
              <p className="ims-modal-sub">
                {t('leaveRequest.reviewSubtitle', {
                  employee: reviewTarget.employeeName,
                  from: formatDate(reviewTarget.startDate),
                  to: formatDate(reviewTarget.endDate),
                })}
              </p>

              <div className="form-group">
                <label className="form-label">
                  {t('leaveRequest.reviewNotes')}
                  <span className="char-counter">{reviewNotes.length}/{MAX_REVIEW_NOTES}</span>
                </label>
                <textarea
                  className="form-textarea"
                  value={reviewNotes}
                  maxLength={MAX_REVIEW_NOTES}
                  onChange={(e) => setReviewNotes(e.target.value.substring(0, MAX_REVIEW_NOTES))}
                  rows={3}
                  placeholder={t('leaveRequest.reviewNotesPlaceholder')}
                />
                <small className="form-helper">{t('leaveRequest.reviewNotesHint')}</small>
              </div>

              <div className="ims-modal-footer">
                <button
                  type="button"
                  className="ims-btn ims-btn-secondary"
                  onClick={() => setReviewTarget(null)}
                  disabled={reviewSaving}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className={`ims-btn ${reviewApprove ? 'ims-btn-success' : 'ims-btn-danger'}`}
                  onClick={submitReview}
                  disabled={reviewSaving}
                >
                  {reviewSaving
                    ? <><span className="ims-btn-spinner" />{t('common.saving')}</>
                    : reviewApprove ? t('leaveRequest.approve') : t('leaveRequest.reject')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LeaveRequestListPage;
